export enum WfOperationStatus {
  Entrapment = 'entrapment',
  OnCall = 'onCall',
  OtherAlert = 'otherAlert',
  PartsReplace = 'partsReplace',
  FloodResponse = 'floodResponse',
  PowerOutageAcOff = 'powerOutageAcOff',
  RecoveryCompleted = 'recoveryCompleted',
  SiteInaccessible = 'siteInaccessible',
  None = 'none',
  Incomplete = 'incomplete',
}

export enum WfOperationStatusCode {
  Entrapment = 'entrapment',
  OnCall = 'oncall',
  StormFloodContact = 'alert',
  OtherAlert = 'otherAlert',
  PartsReplace = 'partsReplace',
  FloodResponse = 'floodResponse',
  PowerOutageAcOff = 'powerOutage',
  RecoveryCompleted = 'recoveryCompleted',
  SiteInaccessible = 'siteInaccessible',
}

export const defaultSatusOrder = [
  WfOperationStatus.None,
  WfOperationStatus.Entrapment,
  WfOperationStatus.PartsReplace,
  WfOperationStatus.FloodResponse,
  WfOperationStatus.OnCall,
  WfOperationStatus.SiteInaccessible,
  WfOperationStatus.PowerOutageAcOff,
  WfOperationStatus.OtherAlert,
  WfOperationStatus.RecoveryCompleted,
  WfOperationStatus.Incomplete,
];

export function sortStatusOptions<T, K extends keyof T>(
  array: readonly T[],
  key: K,
  order: readonly NonNullable<T[K]>[]
): T[] {
  const map = new Map<NonNullable<T[K]>, number>(order.map((v, i) => [v, i]));

  return [...array].sort((a, b) => {
    const av = a[key] as NonNullable<T[K]>;
    const bv = b[key] as NonNullable<T[K]>;

    const ai = map.get(av) ?? Number.POSITIVE_INFINITY;
    const bi = map.get(bv) ?? Number.POSITIVE_INFINITY;
    return ai - bi;
  });
}

export function getOperationStatusTranslationKey(
  status: WfOperationStatus | WfOperationStatusCode
): string {
  return `wf.statusCodes.${status}`;
}

export function getStatusFromStatusCode(statusCode: string): WfOperationStatus {
  if ([WfOperationStatusCode.Entrapment].some((item) => item === statusCode)) {
    return WfOperationStatus.Entrapment;
  }
  if (
    [WfOperationStatusCode.OnCall, WfOperationStatusCode.StormFloodContact].some(
      (item) => item === statusCode
    )
  ) {
    return WfOperationStatus.OnCall;
  }
  if ([WfOperationStatusCode.OtherAlert].some((item) => item === statusCode)) {
    return WfOperationStatus.OtherAlert;
  }
  if ([WfOperationStatusCode.PartsReplace].some((item) => item === statusCode)) {
    return WfOperationStatus.PartsReplace;
  }
  if ([WfOperationStatusCode.FloodResponse].some((item) => item === statusCode)) {
    return WfOperationStatus.FloodResponse;
  }
  if ([WfOperationStatusCode.PowerOutageAcOff].some((item) => item === statusCode)) {
    return WfOperationStatus.PowerOutageAcOff;
  }
  if ([WfOperationStatusCode.RecoveryCompleted].some((item) => item === statusCode)) {
    return WfOperationStatus.RecoveryCompleted;
  }
  if ([WfOperationStatusCode.SiteInaccessible].some((item) => item === statusCode)) {
    return WfOperationStatus.SiteInaccessible;
  }
  return WfOperationStatus.None;
}

export function operationStatusCodes(status: WfOperationStatus) {
  switch (status) {
    case WfOperationStatus.Entrapment:
      return [WfOperationStatusCode.Entrapment];
    case WfOperationStatus.OnCall:
      return [WfOperationStatusCode.OnCall, WfOperationStatusCode.StormFloodContact];
    case WfOperationStatus.OtherAlert:
      return [WfOperationStatusCode.OtherAlert];
    case WfOperationStatus.PartsReplace:
      return [WfOperationStatusCode.PartsReplace];
    case WfOperationStatus.FloodResponse:
      return [WfOperationStatusCode.FloodResponse];
    case WfOperationStatus.PowerOutageAcOff:
      return [WfOperationStatusCode.PowerOutageAcOff];
    case WfOperationStatus.RecoveryCompleted:
      return [WfOperationStatusCode.RecoveryCompleted];
    case WfOperationStatus.SiteInaccessible:
      return [WfOperationStatusCode.SiteInaccessible];
    case WfOperationStatus.Incomplete:
      return [
        WfOperationStatusCode.Entrapment,
        WfOperationStatusCode.PartsReplace,
        WfOperationStatusCode.FloodResponse,
        WfOperationStatusCode.OnCall,
        WfOperationStatusCode.StormFloodContact,
        WfOperationStatusCode.SiteInaccessible,
        WfOperationStatusCode.PowerOutageAcOff,
        WfOperationStatusCode.OtherAlert,
      ];
    case WfOperationStatus.None:
      return [
        WfOperationStatusCode.Entrapment,
        WfOperationStatusCode.PartsReplace,
        WfOperationStatusCode.FloodResponse,
        WfOperationStatusCode.OnCall,
        WfOperationStatusCode.StormFloodContact,
        WfOperationStatusCode.SiteInaccessible,
        WfOperationStatusCode.PowerOutageAcOff,
        WfOperationStatusCode.OtherAlert,
        WfOperationStatusCode.RecoveryCompleted,
      ];
  }
}

export function getMapPinPropsFromStatusCode(code: string) {
  return getMapPinProps(getStatusFromStatusCode(code));
}

export function getMapPinProps(operationStatus: WfOperationStatus) {
  switch (operationStatus) {
    case WfOperationStatus.Entrapment:
      return { fillColor: 'text-red-500', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.SiteInaccessible:
      return { fillColor: 'text-emerald-200', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.RecoveryCompleted:
      return { fillColor: 'text-sky-200', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.PowerOutageAcOff:
      return { fillColor: 'text-green-900', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.OtherAlert:
      return { fillColor: 'text-yellow-300', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.PartsReplace:
      return { fillColor: 'text-pink-400', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.FloodResponse:
      return { fillColor: 'text-pink-400', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.OnCall:
      return { fillColor: 'text-orange-400', lineColor: 'text-black', dotColor: 'bg-white' };
    case WfOperationStatus.None:
      return { fillColor: 'text-white', lineColor: 'text-black', dotColor: 'bg-white' };
    default:
      return { fillColor: 'text-white', lineColor: 'text-black', dotColor: 'bg-white' };
  }
}

/**
 * statusId の優先順位に基づいて AdvancedMarker の zIndex 値を返す。
 * リスト先頭ほど優先度が高く（手前に表示）、リスト外は最背面となる。
 */
const WF_WORKPLACE_MARKER_Z_INDEX_ORDER: readonly string[] = [
  WfOperationStatusCode.Entrapment, // 'entrapment'
  WfOperationStatusCode.PartsReplace, // 'partsReplace'
  WfOperationStatusCode.FloodResponse, // 'floodResponse'
  WfOperationStatusCode.StormFloodContact, // 'alert'
  WfOperationStatusCode.OnCall, // 'oncall'
  WfOperationStatusCode.SiteInaccessible, // 'siteInaccessible'
  WfOperationStatusCode.PowerOutageAcOff, // 'powerOutage'
  WfOperationStatusCode.OtherAlert, // 'otherAlert'
  WfOperationStatusCode.RecoveryCompleted, // 'recoveryCompleted'
];

export function getWfWorkplaceMarkerZIndex(statusId: string | null | undefined): number {
  if (statusId == null) return 0;
  const index = WF_WORKPLACE_MARKER_Z_INDEX_ORDER.indexOf(statusId);
  if (index === -1) return 0;
  return WF_WORKPLACE_MARKER_Z_INDEX_ORDER.length - index;
}
